package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.ShotPushCheckDto
import dev.lingvo.shared.ShotCheckSeverity
import dev.lingvo.shared.ShotPushJobDto
import dev.lingvo.shared.ShotPushPreflightDto
import dev.lingvo.shared.ShotPushState
import dev.lingvo.shared.ShotSetApprovalDto
import dev.lingvo.shared.StoreShotsDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tPlural
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.attributes.InputType
import dev.lingvo.site.ds.ControlHeight
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.components.plainLocaleLabel

/** Review binds selection, server facts and consent into the same set that S2 will receive. */
@Composable
fun PushReview(
    slug: String, platform: StorePlatform, rows: List<ReviewSetRow>, selected: Set<ReviewSetKey>,
    approval: ShotSetApprovalDto?, preflight: ShotPushPreflightDto?, storeImages: StoreShotsDto?,
    job: ShotPushJobDto?, canManage: Boolean, busy: Boolean,
    onSelection: (Set<ReviewSetKey>) -> Unit, onApprove: () -> Unit, onPush: () -> Unit,
    onCancel: () -> Unit, onRetry: () -> Unit, renderChecks: List<ShotPushCheckDto> = emptyList(),
) {
    val palette = ColorMode.current.toSitePalette()
    var expanded by remember { mutableStateOf<ReviewSetKey?>(null) }
    var previewRetry by remember(expanded) { mutableStateOf(0) }
    val pushable = selected.intersect(defaultScreenshotPushSelection(rows))
    val running = job?.state in setOf(ShotPushState.QUEUED, ShotPushState.RUNNING, ShotPushState.WAITING_FOR_VERSION)
    val approvalMatches = approval?.valid == true && rows.filter { it.key in pushable }.flatMap { it.renders }.all { image ->
        approval.items.any { it.renderId == image.id && it.sha256 == image.sha256 }
    }
    val reason = screenshotPushBlockedReason(pushable.isNotEmpty(), approval != null, approvalMatches,
        selectionIsRectangle(pushable), preflight, running)
    // Consent is pinned to this exact snapshot. A refreshed selection, approval or preflight closes it.
    val confirmationKey = listOf(pushable, approval, preflight, job?.state, busy)
    var confirming by remember(confirmationKey) { mutableStateOf(false) }
    val platformName = if (platform == StorePlatform.APP_STORE) "App Store" else "Google Play"
    val languageCount = pushable.map { it.locale }.distinct().size
    Div(Modifier.styleModifier { property("min-width", "0"); property("display", "grid"); property("gap", Space.LG.toString()) }.toAttrs { attr("data-push-review", "") }) {
        LSectionHeading(t("app.projects.slug.screenshots.push_review.push_summary", "Push summary"), description = "")
        LMuted(t("app.projects.slug.screenshots.push_review.tick_the_sets_to_approve_and", "Tick the sets to approve and push."))
        LTable(modifier = Modifier.styleModifier { property("min-width", "0"); property("max-width", "100%") }, caption = t("app.projects.slug.screenshots.push_review.languages_and_devices", "Languages and devices"), tableModifier = Modifier.styleModifier { property("min-width", "100%") }) {
            LTableHead { LTableRow {
                if (canManage) LTableHeaderCell { Text(t("app.projects.slug.screenshots.push_review.select", "Select")) }
                LTableHeaderCell { Text(t("app.projects.slug.screenshots.push_review.screenshot_set", "Screenshot set")) }
                LTableHeaderCell { Text(t("app.projects.slug.screenshots.push_review.status", "Status")) }
            } }
            LTableBody { rows.forEach { row ->
                LTableRow(selected = row.key in selected) {
                    if (canManage) LTableCell(Modifier.styleModifier { property("padding-inline", Space.SM.toString()) }) {
                        val label = tf("shots.review.select_row", "Select {locale} · {device}",
                            "locale" to plainLocaleLabel(row.key.locale), "device" to screenshotDeviceLabel(row.key.device))
                        Label(attrs = Modifier.styleModifier {
                            property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
                            property("min-width", ControlHeight.MD.toString()); property("min-height", ControlHeight.MD.toString())
                        }.toAttrs()) {
                            Input(InputType.Checkbox, attrs = {
                                checked(row.key in selected)
                                attr("aria-label", label)
                                if (row.renders.isEmpty() || running || busy) attr("disabled", "")
                                style {
                                    property("width", IconSize.LG.toString()); property("height", IconSize.LG.toString()); property("margin", "0")
                                    property("accent-color", palette.accent.toString())
                                }
                                onChange { event -> onSelection(if (event.value) selected + row.key else selected - row.key) }
                            })
                        }
                    }
                    LTableCell {
                        LButton(tf("shots.review.row", "{locale} · {device}", "locale" to plainLocaleLabel(row.key.locale), "device" to screenshotDeviceLabel(row.key.device)),
                            { expanded = if (expanded == row.key) null else row.key }, variant = LButtonVariant.GHOST)
                        val remote = storeImages?.sets?.firstOrNull { it.locale == row.key.locale && it.deviceClass == row.key.device }
                        if (remote?.readable == true && remote.images.all { it.sha256 != null }) {
                            LMuted(tf("shots.review.diff", "{new} new · {same} same · {removed} removed", "new" to row.newCount, "same" to row.sameCount, "removed" to row.removedCount))
                        } else LMuted(t("app.projects.slug.screenshots.push_review.the_current_store_comparison_is_unavailable", "The current store comparison is unavailable."))
                    }
                    LTableCell {
                        val relevant = preflight?.checks?.filter { it.cell == null || (it.cell?.locale == row.key.locale && it.cell?.deviceClass == row.key.device) }
                        val blocked = !row.ready || relevant?.any { it.severity == ShotCheckSeverity.RED } == true
                        val waiting = preflight == null || !preflight.editable || relevant?.any { it.severity == ShotCheckSeverity.AMBER } == true
                        LBadge(when {
                            blocked -> t("app.projects.slug.screenshots.push_review.blocked", "Blocked")
                            waiting -> t("app.projects.slug.screenshots.push_review.waiting", "Waiting")
                            else -> t("app.projects.slug.screenshots.push_review.ready", "Ready")
                        }, variant = when { blocked -> LBadgeVariant.DANGER; waiting -> LBadgeVariant.WARNING; else -> LBadgeVariant.SUCCESS })
                    }
                }
            } }
        }
        rows.firstOrNull { it.key == expanded }?.let { row ->
            val urls = renderObjectUrls(slug, platform, row.renders, previewRetry)
            BeforeAfterStrip(storeImages?.sets?.firstOrNull { it.locale == row.key.locale && it.deviceClass == row.key.device }, row, urls.urls)
            if (urls.failed.isNotEmpty()) {
                LMuted(t("app.projects.slug.screenshots.push_review.some_screenshot_previews_could_not_load", "Some screenshot previews could not load. Retry to see the complete set."))
                LButton(t("app.projects.slug.screenshots.push_review.retry_previews", "Retry previews"), { previewRetry++ }, variant = LButtonVariant.SECONDARY)
            }
        }
        LSectionHeading(t("app.projects.slug.screenshots.push_review.checks", "Checks"), description = "")
        PushChecks(slug, preflight, pushable.isNotEmpty(), approval != null, renderChecks)
        SetApproval(approval, canManage && pushable.isNotEmpty() && rows.filter { it.key in pushable }.all { it.ready } && selectionIsRectangle(pushable) && !running, busy, onApprove, visible = canManage)
        LAlert(title = screenshotPushNotice(platform), variant = LAlertVariant.WARNING)
        if (canManage) {
            Div(Modifier.styleModifier { property("position", if (reason == null) "sticky" else "static"); property("bottom", "0") }.toAttrs()) {
                LButton(tPlural("shots.review.push_languages", languageCount, "Push {count} language to {platform}", "Push {count} languages to {platform}", "platform" to if (platform == StorePlatform.APP_STORE) "the App Store" else "Google Play"),
                    { if (reason == null && !busy) confirming = true }, Modifier.fillMaxWidth(), enabled = reason == null && !busy, loading = busy)
                reason?.let { LMuted(it) }
            }
            LConfirmDialog(open = confirming, title = tf("shots.review.confirm", "Push screenshots to the {platform}?", "platform" to platformName),
                confirmLabel = t("app.projects.slug.screenshots.push_review.push_now", "Push now"), onConfirm = { if (reason == null && !busy) { confirming = false; onPush() } },
                onCancel = { confirming = false }, tone = LConfirmTone.DANGER,
                consequenceTitle = t("app.projects.slug.screenshots.push_review.push_summary", "Push summary"),
                consequence = tf("shots.review.consequence", "This replaces {images} in {languages} across {sets}.",
                    "images" to tPlural("shots.review.image_count", rows.filter { it.key in pushable }.sumOf { it.renders.size }, "{count} screenshot", "{count} screenshots"),
                    "languages" to tPlural("shots.review.language_count", languageCount, "{count} language", "{count} languages"),
                    "sets" to tPlural("shots.review.set_count", pushable.size, "{count} device set", "{count} device sets")),
                busy = busy)
        }
        PushResults(job, canManage, onCancel, onRetry)
    }
}
